import { z } from "zod";

/*
  EFFECTS. What a special move or an item does in a battle, as data. Moves
  (ninjas.ts) and items (items.ts) share these kinds, so a new move or item is
  a row, not code. Nothing here answers a question: every kind makes answers
  count for more, forgives a slip, or buys time. Skip is the one exception
  and it's in the brief, once as a move and once as an item.
*/

export const EFFECT_KINDS = [
  "time",
  "shield",
  "charge",
  "skip",
  "boost",
  "slow",
  "hints",
  "streak",
  "coins",
] as const;
export type EffectKind = (typeof EFFECT_KINDS)[number];

export const EffectSchema = z.discriminatedUnion("kind", [
  /** More time on the clock. */
  z.object({ kind: z.literal("time"), seconds: z.number().int().positive() }),
  /** The next few wrong answers don't hurt. */
  z.object({ kind: z.literal("shield"), hits: z.number().int().positive() }),
  /** The next few right answers hit harder. */
  z.object({
    kind: z.literal("charge"),
    hits: z.number().int().positive(),
    times: z.number().positive(),
  }),
  /** Skip this question, no penalty. */
  z.object({ kind: z.literal("skip") }),
  /** Harder hits for a while. */
  z.object({
    kind: z.literal("boost"),
    times: z.number().positive(),
    seconds: z.number().int().positive(),
  }),
  /** The clock runs at a fraction of its speed for a while (0.5 is half speed). */
  z.object({
    kind: z.literal("slow"),
    factor: z.number().positive().max(1),
    seconds: z.number().int().positive(),
  }),
  /** More Show me's this battle. */
  z.object({ kind: z.literal("hints"), count: z.number().int().positive() }),
  /** Seconds added for every `every` right answers in a row. */
  z.object({
    kind: z.literal("streak"),
    every: z.number().int().positive(),
    seconds: z.number().int().positive(),
  }),
  /** A share added to the coins a battle pays (0.2 is +20%). */
  z.object({ kind: z.literal("coins"), share: z.number().positive() }),
]);
export type Effect = z.infer<typeof EffectSchema>;

/** Effects that apply once as the fight starts, rather than when pressed. */
export const START_KINDS: readonly EffectKind[] = ["time", "shield", "hints"];
